<template>
    <hts-map
        ref="map"
        :center-position="centerPosition"
        :minus-height="minusHeight"
        @click="handleClick" />
</template>

<script lang="ts" setup>
interface IRoute {
  originData: google.maps.LatLngLiteral
  destinationData: google.maps.LatLngLiteral
  waypointsData: {
    location: google.maps.LatLngLiteral
  }[]
}

const props = defineProps({
  mapStyle: {
    type: String,
    default: ''
  },
  centerPosition: Object,
  centerMarker: Boolean,
  nodeList: {
    type: Array,
    default: () => []
  },
  route: {
    type: Object || null,
    default: null
  },
  minusHeight: {
    type: Number,
    default: 0
  }
})
const emits = defineEmits(['click', 'clickMarker'])
const map: any = ref(null)
const { nodeList, centerPosition, centerMarker, route } = toRefs(props)

const handleClick = (location: google.maps.LatLng | google.maps.LatLngLiteral, place: google.maps.places.PlaceResult) => {
  emits('click', location, place)
}

onMounted(() => {
  renderNodeList()
  renderRoute()
  renderCenterPosition()
})

watch(
  () => centerMarker.value,
  () => {
    renderCenterPosition()
  },
  { deep: true }
)

const renderCenterPosition = () => {
  if (!centerPosition?.value) {
    return
  }
  map.value?.ready().then(() => {
    if (centerPosition?.value) {
      map.value.createAdvanceMarker(centerPosition.value as google.maps.LatLng | google.maps.LatLngLiteral)
    }
  })
}

watch(
  () => centerPosition?.value,
  (val: Object) => {
    map.value &&
      map.value.ready().then(() => {
        if (centerPosition?.value) {
          map.value.removeMarkers()
          map.value.createMarker(val as google.maps.LatLng | google.maps.LatLngLiteral)
        }
      })
  },
  { deep: true }
)

watch(
  () => nodeList.value,
  () => {
    map.value.removeAllMarkers()
    renderNodeList()
  },
  { deep: true }
)

watch(
  () => route.value,
  (val: any) => {
    if (!val) {
      if (!map.value) {
        return
      }
      map.value.clearDirection()
    }
    renderRoute()
  },
  { deep: true }
)

const renderNodeList = () => {
  if (!map.value) {
    return
  }
  map.value.ready().then(() => {
    map.value.removeAllMarkers()

    nodeList.value.forEach(async (node: any) => {
      const innerHtml = node.isNewLocation
        ? `
<div><?xml version="1.0" encoding="iso-8859-1"?>
<!-- Uploaded to: SVG Repo, www.svgrepo.com, Generator: SVG Repo Mixer Tools -->
<svg fill="currentColor" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"
	 viewBox="0 0 34.398 34.398" xml:space="preserve">
<g>
	<g id="c28_geolocalization">
		<path d="M17.202,0C10.749,0,5.515,5.197,5.515,11.607c0,3.281,3.218,9.156,3.218,9.156l8.039,13.635l8.386-13.477
			c0,0,3.726-5.605,3.726-9.314C28.883,5.197,23.653,0,17.202,0z M17.147,18.002c-3.695,0-6.688-2.994-6.688-6.693
			c0-3.693,2.993-6.686,6.688-6.686c3.693,0,6.69,2.992,6.69,6.686C23.837,15.008,20.84,18.002,17.147,18.002z"/>
		<polygon points="18.539,7.233 15.898,7.233 15.898,10.242 12.823,10.242 12.823,12.887 15.898,12.887 15.898,15.985
			18.539,15.985 18.539,12.887 21.576,12.887 21.576,10.242 18.539,10.242 		"/>
	</g>
	<g id="Capa_1_146_">
	</g>
</g>
</svg></div>`
        : `<div><svg  xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" viewBox="0 0 256 256" xml:space="preserve">
<defs>
</defs>
<g style="stroke: none; stroke-width: 0; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: currentColor; fill-rule: nonzero; opacity: 1;" transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)" >
	<path d="M 45 0 C 27.395 0 13.123 14.272 13.123 31.877 c 0 7.86 2.858 15.043 7.573 20.6 L 45 81.101 l 24.304 -28.624 c 4.716 -5.558 7.573 -12.741 7.573 -20.6 C 76.877 14.272 62.605 0 45 0 z M 45 43.889 c -7.24 0 -13.11 -5.869 -13.11 -13.11 c 0 -7.24 5.869 -13.11 13.11 -13.11 s 13.11 5.869 13.11 13.11 C 58.11 38.02 52.24 43.889 45 43.889 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: currentColor; fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
	<path d="M 58.958 71.559 L 45 82.839 L 31.057 71.556 c -9.329 1.65 -15.682 4.901 -15.682 8.645 c 0 5.412 13.263 9.8 29.625 9.8 c 16.361 0 29.625 -4.388 29.625 -9.8 C 74.625 76.458 68.278 73.209 58.958 71.559 z" style="stroke: none; stroke-width: 1; stroke-dasharray: none; stroke-linecap: butt; stroke-linejoin: miter; stroke-miterlimit: 10; fill: currentColor; fill-rule: nonzero; opacity: 1;" transform=" matrix(1 0 0 1 0 0) " stroke-linecap="round" />
</g>
</svg></div>`
      const htmlContent = document.createElement('div')
      htmlContent.className = 'hts-marker  text-blue-500' + (node.className || '')
      const text = document.createElement('span')
      const icon = document.createElement('img')
      htmlContent.innerHTML = innerHtml

      text.textContent = node.title || $t('labels.unknown')
      htmlContent.appendChild(icon)
      htmlContent.appendChild(text)
      const marker = await map.value.createAdvanceMarker(node.position as google.maps.LatLng | google.maps.LatLngLiteral, htmlContent)
      marker.addListener('click', () => {
        emits('clickMarker', node)
        map.value.hideInfoWindow()
        map.value.hideClickPositionMarker()
      })
    })
  })
}

const renderRoute = () => {
  if (!route.value) {
    return
  }

  if (!map.value) {
    return
  }

  map.value.ready().then(() => {
    const { originData, destinationData, waypointsData } = route.value
    map.value && map.value.drawDirection(originData, destinationData, 'DRIVING', waypointsData)
  })
}
</script>
